import React, { useState } from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { IconDictionary } from "../Icon/types";
import profilePicStyles from "../ProfilePic/ProfilePic.scss";

import styles from "./Facepile.scss";
import { FacepileFaces } from "./FacepileFaces";
import { FacepileWithPopover } from "./FacepileWithPopover";

export type Size = "large" | "medium" | "small";
export type ListType = undefined | "name" | "all";

export interface FacepilePerson {
  avatar?: string;
  department?: string;
  id?: string;
  image160?: string;
  image30?: string;
  image70?: string;
  initials?: string;
  jobTitle?: string;
  name?: string;
  /** @deprecated */
  primaryGroup?: string;
  profileUrl?: string;
  featuredGroup?: React.ReactElement;
  /** Size of the faces */
  size?: Size;
  /** Whether to show a list of all faces with or without secondary text */
  list?: ListType;
}

export interface FacepileProps {
  /** The users whose faces we want to show */
  users: FacepilePerson[];
  /** The maximum number of faces to show */
  max?: number;
  /** @deprecated */
  dimensions?: number;
  /** The tag to show to the right of the faces */
  tag?: string;
  /** Size of the faces */
  size?: Size;
  /** Whether to show a list of all faces with or without secondary text */
  list?: ListType;
  /** Opens user profile in a new tab */
  openProfileInNewTab?: boolean;
  /** Disables the link to the user's profile */
  disableProfileLink?: boolean;
  /** If provided this icon will show on the profile pictures instead of images or initials */
  profilePicIconOverride?: IconDictionary;
  /** If provided overrides the people count */
  overridePeopleCount?: number;
  /** Sets the active state of the facepile (for click state) */
  active?: boolean;
  /** If true, the active face will be highlighted with a box shadow */
  highlightActiveFace?: boolean;
  /** Callback for when a user is clicked */
  onUserClick?: (user: FacepilePerson) => void;
  /** Callback for when the user count is clicked */
  onUserCountClick?: () => void;
}

function sizeFromDimensionsOrSize(
  dimensions: number | undefined,
  size?: Size
): "large" | "medium" | "small" {
  if (!dimensions) {
    return coalesce(size, "large" as const);
  } else if (dimensions < 32) {
    return "small";
  } else if (dimensions === 40) {
    return "medium";
  }
  return "large";
}

export const circleClass = (
  dimensions: number | undefined,
  size?: Size
): [string[], string] => {
  const sizeStr = sizeFromDimensionsOrSize(dimensions, size);
  return [
    [styles[`userCircle--${sizeStr}`], profilePicStyles[`wrapper--${sizeStr}`]],
    sizeStr,
  ];
};

export function Facepile({
  users,
  max,
  dimensions,
  size = "large",
  tag = "",
  list = undefined,
  openProfileInNewTab = false,
  disableProfileLink = false,
  profilePicIconOverride,
  overridePeopleCount = 0,
  active = false,
  highlightActiveFace = false,
  onUserClick,
  onUserCountClick,
}: FacepileProps): React.ReactElement {
  const [activeUserId, setActiveUserId] = useState<string>("");
  const actualSize = sizeFromDimensionsOrSize(dimensions, size);
  if (max === undefined) {
    if (actualSize === "large") {
      max = 7;
    } else {
      max = 5;
    }
  }

  let facesWithoutOverflow = max;
  const totalUsers = overridePeopleCount || users.length;

  if (actualSize === "large" && totalUsers > max) {
    facesWithoutOverflow = max - 1;
  } else if (totalUsers > max) {
    facesWithoutOverflow = max - 1;
  }

  const [circleClassNames] = circleClass(dimensions, size);
  const [countCircleClassNames, countOverflowSize] = circleClass(
    dimensions,
    "small"
  );
  const overflowClassNames = cx(
    countCircleClassNames,
    countOverflowSize,
    styles.overflow,
    {
      [styles["overflow-expanded"]]: totalUsers - facesWithoutOverflow > 99,
      [styles["overflow-active"]]:
        active && !(highlightActiveFace && activeUserId),
    }
  );
  const wrapperClass = cx(styles.facepile, styles[`facepile--${actualSize}`], {
    [styles["facepile--tag"]]: Boolean(tag),
    [styles["facepile--active"]]: active,
  });

  const showAll = list === "all";

  const handleUserCountClick = () => {
    setActiveUserId("");
    onUserCountClick?.();
  };

  const handleUserClick = (user: FacepilePerson) => {
    setActiveUserId(coalesce(user.id, ""));
    onUserClick?.(user);
  };

  if (list) {
    return (
      <FacepileWithPopover
        circleClassNames={circleClassNames}
        dimensions={dimensions}
        max={max}
        overflowClassNames={overflowClassNames}
        size={actualSize}
        tag={tag}
        users={users}
        wrapperClass={wrapperClass}
        showAll={showAll}
        openProfileInNewTab={openProfileInNewTab}
        disableProfileLink={disableProfileLink}
        profilePicIconOverride={profilePicIconOverride}
        overridePeopleCount={overridePeopleCount}
        active={active}
        highlightActiveFace={highlightActiveFace}
        activeUserId={activeUserId}
        onUserClick={handleUserClick}
        onUserCountClick={handleUserCountClick}
      />
    );
  }

  return (
    <FacepileFaces
      circleClassNames={circleClassNames}
      dimensions={dimensions}
      max={max}
      overflowClassNames={overflowClassNames}
      size={actualSize}
      tag={tag}
      users={users}
      wrapperClass={wrapperClass}
      profilePicIconOverride={profilePicIconOverride}
      overridePeopleCount={overridePeopleCount}
      active={active}
      highlightActiveFace={highlightActiveFace}
      activeUserId={activeUserId}
      onUserClick={handleUserClick}
      onUserCountClick={handleUserCountClick}
    />
  );
}
